import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Sparkles, Check, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { VARIANTS, setVariantOverride } from "../lib/cardVariants";
import { STYLE_PACKS, isStylePackV2, setRecipeOverride, CUSTOM_RECIPE_VARIANT_ID } from "../lib/stylePacks";
import { uploadCustomBackground, removeCustomBackground } from "../lib/api";
import { tap } from "../lib/haptic";
import { track } from "../lib/track";

/**
 * RestyleDrawer — premium feature to swap a card's visual recipe.
 *
 * Two modes:
 *   • V2 (when card.pack is in STYLE_PACKS) — shows the 4 RECIPE
 *     VARIANTS for that pack (Golden Hour, Midnight Film, etc.).
 *     The first variant is free; the rest are premium-gated. Selection
 *     persists via `setRecipeOverride(moodId, "<pack>.<variant>")`.
 *   • Legacy (older cards on cinematic/polaroid/etc. without v2 entries)
 *     — falls back to the old CSS-filter VARIANTS array.
 *
 * Props:
 *   open                — drawer visibility
 *   onClose             — close handler
 *   moodId              — required; localStorage key
 *   pack                — card's pack id (drives which mode)
 *   currentVariantId    — id of currently applied variant/recipe
 *   onVariantChange     — fired after a change so parent re-renders
 *   thumbnailBg         — fallback bg url for legacy mode thumbnails
 *   isPremium           — gate flag
 *   onPremiumLocked     — called when a free user taps a locked tile
 */
export default function RestyleDrawer({
    open,
    onClose,
    moodId,
    pack,
    currentVariantId,
    onVariantChange,
    onCustomBackgroundChange,
    customBackgroundUrl = null,
    thumbnailBg,
    isPremium = false,
    onPremiumLocked,
}) {
    const [pendingId, setPendingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const v2 = isStylePackV2(pack);
    const packDef = v2 ? STYLE_PACKS[pack] : null;
    const tiles = v2
        ? packDef.variants.map((v, i) => ({
              id: v.id,
              label: v.label,
              thumbStyle: { background: v.background },
              isFree: i === 0, // first variant is free
              textColor: v.textColor,
          }))
        : VARIANTS.map((v) => ({
              id: v.id,
              label: v.label,
              thumbStyle: {
                  background: thumbnailBg || "linear-gradient(135deg, #2d3344 0%, #14161c 100%)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: v.filter !== "none" ? v.filter : undefined,
              },
              isFree: v.id === "default",
              textColor: null,
          }));

    const handlePick = (tile) => {
        if (!tile.isFree && !isPremium) {
            track("restyle_locked_tile_clicked", { id: tile.id, pack });
            if (onPremiumLocked) onPremiumLocked(tile);
            return;
        }
        tap();
        setPendingId(tile.id);
        if (v2) {
            setRecipeOverride(moodId, `${pack}.${tile.id}`);
        } else {
            setVariantOverride(moodId, tile.id === "default" ? null : tile.id);
        }
        track("restyle_applied", { id: tile.id, pack, premium: isPremium, v2 });
        if (onVariantChange) onVariantChange(tile);
        setTimeout(() => {
            setPendingId(null);
            onClose();
        }, 700);
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                        data-testid="restyle-drawer-backdrop"
                    />

                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: "spring", stiffness: 320, damping: 32 }}
                        className="fixed bottom-0 left-0 right-0 z-[60] lg:inset-0 lg:flex lg:items-center lg:justify-center lg:pointer-events-none"
                        data-testid="restyle-drawer"
                    >
                        <div
                            className="relative rounded-t-3xl lg:rounded-3xl bg-surface-1 border-t lg:border border-app/20 px-5 pt-5 pb-8 max-w-md mx-auto lg:max-w-lg lg:w-full lg:pointer-events-auto"
                            style={{ boxShadow: "0 -20px 60px rgba(0,0,0,0.45)" }}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-amber-200/80" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-2">
                                        restyle
                                    </span>
                                </div>
                                <button
                                    onClick={onClose}
                                    data-testid="restyle-drawer-close"
                                    aria-label="Close"
                                    className="btn-press p-2 text-app-3 hover:text-app transition-colors"
                                >
                                    <X className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>

                            <p className="font-serif italic text-app text-[18px] leading-snug mb-1">
                                {v2 ? `swap the look within ${packDef.label}.` : "swap the mood, keep the words."}
                            </p>
                            {v2 && (
                                <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 mb-4 opacity-75">
                                    {packDef.description}
                                </p>
                            )}

                            <div className={`grid ${v2 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-3`} data-testid="restyle-variant-grid">
                                {tiles.map((tile) => {
                                    const isCurrent = tile.id === currentVariantId;
                                    const isLocked = !tile.isFree && !isPremium;
                                    return (
                                        <button
                                            key={tile.id}
                                            onClick={() => handlePick(tile)}
                                            data-testid={`restyle-variant-${tile.id}`}
                                            data-locked={isLocked ? "true" : "false"}
                                            className={`btn-press relative rounded-xl overflow-hidden border transition-all ${
                                                isCurrent
                                                    ? "border-amber-300/60 shadow-[0_0_20px_rgba(212,175,110,0.25)]"
                                                    : "border-app/20 hover:border-app/40"
                                            }`}
                                            style={{ aspectRatio: "3 / 4" }}
                                        >
                                            <div className="absolute inset-0" style={tile.thumbStyle} />
                                            {!v2 && (
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
                                            )}
                                            <div className="absolute bottom-2 left-2 right-2 text-left">
                                                <p
                                                    className="font-serif italic text-[11px] leading-tight"
                                                    style={{
                                                        color: v2 && tile.textColor ? tile.textColor : undefined,
                                                        textShadow: v2 ? "0 1px 4px rgba(0,0,0,0.35)" : undefined,
                                                    }}
                                                >
                                                    {tile.label}
                                                </p>
                                            </div>
                                            {isLocked && (
                                                <div className="absolute top-2 right-2 flex items-center justify-center h-6 w-6 rounded-full bg-black/55 backdrop-blur-sm">
                                                    <Lock className="w-3 h-3 text-amber-200/85" strokeWidth={1.8} />
                                                </div>
                                            )}
                                            {isCurrent && !pendingId && (
                                                <div className="absolute top-2 left-2 flex items-center justify-center h-6 w-6 rounded-full bg-amber-300/85">
                                                    <Check className="w-3 h-3 text-stone-900" strokeWidth={2.4} />
                                                </div>
                                            )}
                                            {pendingId === tile.id && (
                                                <motion.div
                                                    initial={{ scale: 0.7, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    className="absolute inset-0 flex items-center justify-center bg-amber-300/20 backdrop-blur-[1px]"
                                                >
                                                    <Check className="w-7 h-7 text-amber-200" strokeWidth={2.2} />
                                                </motion.div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Custom user-uploaded background — Premium feature.
                                Shown only for Style Pack v2 packs (legacy packs
                                use static pack images and can't be overridden). */}
                            {v2 && (
                                <div className="mt-3 flex items-center gap-2" data-testid="restyle-custom-upload-row">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        className="hidden"
                                        data-testid="restyle-custom-upload-input"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = "";
                                            if (!file) return;
                                            if (file.size > 5 * 1024 * 1024) {
                                                toast("Image must be 5MB or smaller.");
                                                return;
                                            }
                                            setUploading(true);
                                            try {
                                                tap();
                                                await uploadCustomBackground(moodId, file);
                                                setRecipeOverride(moodId, `${pack}.${CUSTOM_RECIPE_VARIANT_ID}`);
                                                track("custom_background_uploaded", { mood_id: moodId, pack, size: file.size });
                                                if (onCustomBackgroundChange) onCustomBackgroundChange();
                                                toast("Your photo is the backdrop now.", { duration: 2400 });
                                                setTimeout(() => onClose(), 700);
                                            } catch (err) {
                                                const code = err?.response?.status;
                                                if (code === 402) {
                                                    if (onPremiumLocked) onPremiumLocked({ id: "custom-bg" });
                                                } else if (code === 403) {
                                                    toast("Sign in and claim this card to customize it.");
                                                } else if (code === 413) {
                                                    toast("Image must be 5MB or smaller.");
                                                } else if (code === 400) {
                                                    toast("Try a JPG, PNG, or WebP image.");
                                                } else {
                                                    toast("Upload failed — please try again.");
                                                }
                                            } finally {
                                                setUploading(false);
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        data-testid="restyle-upload-button"
                                        disabled={uploading}
                                        onClick={() => {
                                            if (!isPremium) {
                                                track("custom_bg_locked_clicked", { pack });
                                                if (onPremiumLocked) onPremiumLocked({ id: "custom-bg" });
                                                return;
                                            }
                                            fileInputRef.current?.click();
                                        }}
                                        className={`btn-press flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border transition-colors ${
                                            customBackgroundUrl
                                                ? "border-amber-300/40 bg-amber-400/10 text-amber-100"
                                                : "border-app/20 bg-surface-1 text-app-2 hover:text-app hover:border-app/45"
                                        } font-sans text-[10px] uppercase tracking-[0.32em]`}
                                    >
                                        <ImagePlus className="w-3 h-3" strokeWidth={1.6} />
                                        <span>{uploading ? "uploading…" : customBackgroundUrl ? "replace photo" : "use your photo"}</span>
                                        {!isPremium && <Lock className="w-3 h-3 opacity-80" strokeWidth={1.6} />}
                                    </button>
                                    {customBackgroundUrl && isPremium && (
                                        <button
                                            type="button"
                                            data-testid="restyle-remove-custom-button"
                                            disabled={uploading}
                                            onClick={async () => {
                                                tap();
                                                try {
                                                    await removeCustomBackground(moodId);
                                                    setRecipeOverride(moodId, null);
                                                    track("custom_background_removed", { mood_id: moodId, pack });
                                                    if (onCustomBackgroundChange) onCustomBackgroundChange();
                                                    toast("photo cleared.", { duration: 2000 });
                                                } catch {
                                                    toast("Could not remove — try again.");
                                                }
                                            }}
                                            aria-label="Remove custom background"
                                            className="btn-press flex items-center justify-center w-10 h-10 rounded-xl border border-app/20 bg-surface-1 text-app-3 hover:text-rose-400 hover:border-rose-400/40 transition-colors"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                        </button>
                                    )}
                                </div>
                            )}

                            {!isPremium && (
                                <p className="mt-4 text-center font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 opacity-75">
                                    {v2
                                        ? <>1 free look · <span className="text-amber-200/85">unlock 3 more + every other pack with premium</span></>
                                        : <>free · as captured · <span className="text-amber-200/85">unlock 5 more with premium</span></>}
                                </p>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
